import React, { createContext, useContext, useState, useEffect, useCallback } from "react";

type ConsentStatus = "pending" | "accepted" | "rejected";

interface CookieConsentContextType {
  consentStatus: ConsentStatus;
  acceptCookies: () => void;
  rejectCookies: () => void;
  resetConsent: () => void;
  analyticsEnabled: boolean;
}

const CookieConsentContext = createContext<CookieConsentContextType | undefined>(undefined);

const CONSENT_COOKIE_NAME = "cookie_consent";
const CONSENT_EXPIRY_DAYS = 365;

export const CookieConsentProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [consentStatus, setConsentStatus] = useState<ConsentStatus>("pending");

  // Check for existing consent on mount
  useEffect(() => {
    const savedConsent = localStorage.getItem(CONSENT_COOKIE_NAME);
    if (savedConsent === "accepted") {
      setConsentStatus("accepted");
    } else if (savedConsent === "rejected") {
      setConsentStatus("rejected");
    }
  }, []);

  const acceptCookies = useCallback(() => {
    localStorage.setItem(CONSENT_COOKIE_NAME, "accepted");
    setConsentStatus("accepted");
    // Here you would enable analytics/tracking scripts
    console.log("Cookie consent accepted - analytics enabled");
  }, []);

  const rejectCookies = useCallback(() => {
    localStorage.setItem(CONSENT_COOKIE_NAME, "rejected");
    setConsentStatus("rejected");
    // Ensure no tracking cookies are set
    console.log("Cookie consent rejected - analytics disabled");
  }, []);

  const resetConsent = useCallback(() => {
    localStorage.removeItem(CONSENT_COOKIE_NAME);
    setConsentStatus("pending");
  }, []);

  // Analytics are only enabled when consent is accepted
  const analyticsEnabled = consentStatus === "accepted";

  return (
    <CookieConsentContext.Provider
      value={{
        consentStatus,
        acceptCookies,
        rejectCookies,
        resetConsent,
        analyticsEnabled,
      }}
    >
      {children}
    </CookieConsentContext.Provider>
  );
};

export const useCookieConsent = () => {
  const context = useContext(CookieConsentContext);
  if (context === undefined) {
    throw new Error("useCookieConsent must be used within a CookieConsentProvider");
  }
  return context;
};
